import { failure, success, SuccessOrFailure } from '@sparelabs/core'
import { Geography } from '@sparelabs/geography'
import { uniq } from 'lodash'
import { IRequestRecurrence } from '../types'
import { TripGroupDirection, TripGroupSlotType } from '../types/TripGroupTypes'

export class TripGroupValidator {
  public static validateFeasibility(
    recurrences: IRequestRecurrence[],
    direction: TripGroupDirection,
  ): SuccessOrFailure<TripGroupSlotType> {
    if (recurrences.length === 0) {
      return failure('No recurrences provided')
    }

    if (direction === TripGroupDirection.Inbound && recurrences.some((recurrence) => recurrence.inboundTime === null)) {
      return failure('Recurrence does not have an inbound time')
    }

    // check all serviceIds are the same
    if (uniq(recurrences.map((recurrence) => recurrence.serviceId)).length !== 1) {
      return failure("Services don't match")
    }

    if (this.validateForPickup(recurrences, direction)) {
      return success(TripGroupSlotType.Pickup)
    }

    if (this.validateForDropoff(recurrences, direction)) {
      return success(TripGroupSlotType.Dropoff)
    }

    return failure('Location or time mismatch on both pickup and dropoff.')
  }

  private static validateForPickup(recurrences: IRequestRecurrence[], direction: TripGroupDirection): boolean {
    const pickupRecurrenceData = recurrences.map((recurrence) => ({
      time: direction === TripGroupDirection.Outbound ? recurrence.outboundTime : recurrence.inboundTime,
      intent: direction === TripGroupDirection.Outbound ? recurrence.outboundTimeIntent : recurrence.inboundTimeIntent,
      // pickup address and location are reversed for inbound compared to outbound
      pickupLocation:
        direction === TripGroupDirection.Outbound
          ? recurrence.requestedPickupLocation
          : recurrence.requestedDropoffLocation,
    }))

    const firstRecurrence = pickupRecurrenceData[0]

    return pickupRecurrenceData.every(
      (recurrence) =>
        recurrence.time === firstRecurrence.time &&
        recurrence.intent === firstRecurrence.intent &&
        Geography.arePointsEqual(recurrence.pickupLocation, firstRecurrence.pickupLocation),
    )
  }

  private static validateForDropoff(recurrences: IRequestRecurrence[], direction: TripGroupDirection): boolean {
    const dropoffRecurrenceData = recurrences.map((recurrence) => ({
      time: direction === TripGroupDirection.Outbound ? recurrence.outboundTime : recurrence.inboundTime,
      intent: direction === TripGroupDirection.Outbound ? recurrence.outboundTimeIntent : recurrence.inboundTimeIntent,
      // dropoff address and location are reversed for inbound compared to outbound
      dropoffLocation:
        direction === TripGroupDirection.Outbound
          ? recurrence.requestedDropoffLocation
          : recurrence.requestedPickupLocation,
    }))

    const firstRecurrence = dropoffRecurrenceData[0]

    return dropoffRecurrenceData.every(
      (recurrence) =>
        recurrence.time === firstRecurrence.time &&
        recurrence.intent === firstRecurrence.intent &&
        Geography.arePointsEqual(recurrence.dropoffLocation, firstRecurrence.dropoffLocation),
    )
  }
}
